import CohesiveLogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { cn } from "@/utils";
import { ToolCall } from "@cohesive/api-client";
import {
  Globe,
  Image,
  LibraryBig,
  ScrollText,
  Search,
  SquareTerminal,
  Video,
} from "lucide-react";
import { motion } from "motion/react";
import { logos } from "virtual:integration-logos";

export const ToolCallIcon = ({ toolCall }: { toolCall: ToolCall }) => {
  const platformDisplayName = toolCall.function.platform_display_name;
  const platformImageUrl = toolCall.function.platform_image_url;

  const hasSvgLogo = logos.includes(toolCall.function.platform_name);

  // Sandboxed bash reports platform_name "cohesive" like the other internal
  // tools, but reads more clearly as a terminal than the brand mark — give it
  // a CLI glyph, matched to the inline rail icon treatment below.
  if (toolCall.function.name === "Tool_E2b_Bash") {
    return <SquareTerminal className="size-[18px] text-n-80" />;
  }

  // A handful of internal toolbox tools also report platform_name "cohesive"
  // but read more clearly with a descriptive glyph than the brand mark in the
  // chain-of-thought rail. Matched to the inline rail icon treatment above.
  switch (toolCall.function.name) {
    case "Tool_Toolbox_Googleai_ImageGenerate":
    case "Tool_Toolbox_Googleai_ImageEdit":
      return <Image className="size-[18px] text-n-80" />;
    case "Tool_Toolbox_Googleai_VideoGenerate":
      return <Video className="size-[18px] text-n-80" />;
    case "Tool_Toolbox_Skill_FileGet":
      return <ScrollText className="size-[18px] text-n-80" />;
  }

  // Internal tools (sandboxed bash, canvas edits, image generation, …) all
  // report platform_name "cohesive". Render the brand mark inline with
  // currentColor so it sits among the rail's lucide glyphs (no dark logo
  // badge) and adapts to light/dark like the rest.
  if (toolCall.function.platform_name === "cohesive") {
    return <CohesiveLogoIcon className="h-[18px] w-auto text-n-80" />;
  }

  if (platformImageUrl) {
    return (
      <img
        src={platformImageUrl}
        alt={platformDisplayName}
        className="size-5"
      />
    );
  }

  if (hasSvgLogo) {
    return (
      <img
        src={`${import.meta.env.VITE_STATIC_BASE_URL || ""}/images/integrations/logos/${toolCall.function.platform_name}.svg`}
        alt={toolCall.function.platform_name}
        width={24}
        height={24}
      />
    );
  }

  switch (toolCall.function.platform_name) {
    case "image-generator":
      return <Image className="text-foreground p-[2px]" size={24} />;
    case "thinking":
      return (
        <div className="flex items-center justify-center">
          <div
            className={cn(
              "relative w-2 h-2 rounded-full",
              "dark:bg-neutral-950 bg-neutral-800",
            )}
          >
            <div className="flex flex-row items-center justify-center transition-opacity duration-200 opacity-100">
              <motion.div
                className="absolute inset-0 dark:bg-neutral-950 bg-neutral-800 rounded-full"
                animate={{
                  scale: [1, 3, 1],
                  opacity: [0.7, 0, 0],
                }}
                transition={{
                  times: [0, 0.9, 1],
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <p className="absolute mt-2 text-md  transition-opacity duration-200 opacity-100 ml-28">
                Thinking...
              </p>
            </div>
          </div>
        </div>
      );
    case "skill_selection":
      return (
        <ScrollText
          className="dark:text-spectrum-violet text-spectrum-violet p-[2px]"
          size={24}
        />
      );
    case "calling_tools":
      return (
        <div className="flex items-center justify-center">
          <div
            className={cn(
              "relative w-2 h-2 rounded-full",
              "dark:bg-neutral-950 bg-neutral-800",
            )}
          >
            <div className="flex flex-row items-center justify-center transition-opacity duration-200 opacity-100">
              <motion.div
                className="absolute inset-0 dark:bg-neutral-950 bg-neutral-800 rounded-full"
                animate={{
                  scale: [1, 3, 1],
                  opacity: [0.7, 0, 0],
                }}
                transition={{
                  times: [0, 0.9, 1],
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </div>
          </div>
        </div>
      );
    case "planning":
      return (
        <div className="flex items-center justify-center">
          <div
            className={cn(
              "relative w-2 h-2 rounded-full",
              "dark:bg-neutral-950 bg-neutral-800",
            )}
          >
            <div className="flex flex-row items-center justify-center transition-opacity duration-200 opacity-100">
              <motion.div
                className="absolute inset-0 dark:bg-neutral-950 bg-neutral-800 rounded-full"
                animate={{
                  scale: [1, 3, 1],
                  opacity: [0.7, 0, 0],
                }}
                transition={{
                  times: [0, 0.9, 1],
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <p className="absolute mt-2 text-md  transition-opacity duration-200 opacity-100 ml-28">
                Planning...
              </p>
            </div>
          </div>
        </div>
      );
    case "web":
      return <Globe className="text-foreground p-[2px]" size={24} />;
    case "rag":
      return <LibraryBig className="text-foreground p-[2px]" size={24} />;
    default:
      return <Search className="text-foreground p-[2px]" size={24} />;
  }
};
